<template>
  <Modal
    :headline="$t('myVocabulary.moveSelectedToTopic', {amount: selectedItems.length, topic: localTopic})"
    :show-modal="showModal"
    :save-btn-name="$t('app.move')"
    :show-full-screen="true"
    :save-btn-disabled="localTopic === topic"
    @save="moveSelectedItems"
    @cancel="showModal = false">
    <v-row align="end">
      <v-col cols="6" class="py-0">
        <v-select
          :label="$t('myVocabulary.topic')"
          hide-details
          :no-data-text="$t('myVocabulary.noTopicCreated')"
          :items="topics"
          v-model="localTopic" />
      </v-col>
      <v-col cols="6" class="py-0">
        <MvTopicCreator
          :vocabulary-list-by-language="vocabularyListByLanguage"
          @create:topic="createTopic" />
      </v-col>
    </v-row>
    <v-btn
      color="orange"
      outlined
      slot="activator"
      block
      :disabled="selectedItems.length === 0"
      @click="onOpen">
      <v-icon left small>open_with</v-icon>
      {{ $t('app.move') }}
      ({{ selectedItems.length }} / {{ totalItemsLength }})
    </v-btn>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '@components/modal';
import MvTopicCreator from '@components/mv_manager/mv_topic_creator';
import { VOCABULARIES } from '@/config/firestore';
import mergeData from '@/mixins/firestore/mergeData';
import overwriteData from '@/mixins/firestore/overwriteData';
import loaderMethods from '@/mixins/loader';

export default {
  name: 'MvMoveItems',
  components: {
    Modal,
    MvTopicCreator,
  },
  mixins: [mergeData, overwriteData, loaderMethods],
  props: {
    vocabularyList: {
      type: Array,
      required: true,
    },
    vocabularyListKeys: {
      type: Array,
      required: true,
    },
    selectedItems: {
      type: Array,
      required: true,
    },
    totalItemsLength: {
      type: Number,
      required: true,
    },
    topic: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    showModal: false,
    topics: [],
    localTopic: '',
    baseTopic: '',
    color: 'blue',
  }),
  computed: {
    ...mapGetters('user', ['userLanguages']),

    vocabularyListByLanguage() {
      const userLanguage = Object.values(this.userLanguages).join('-');
      return this.vocabularyList.filter((item) => item.lang === userLanguage);
    },
  },
  methods: {
    async onOpen() {
      this.showLoader();

      this.showModal = true;
      this.baseTopic = '';
      this.localTopic = this.topic;

      this.getTopics();

      this.hideLoader();
    },
    createTopic(baseTopic, color) {
      this.baseTopic = baseTopic;
      this.localTopic = baseTopic;
      this.color = color;

      this.topics.push(baseTopic);
    },
    async moveVocabularyToNewTopic() {
      const lastVocabularyListKey = this.vocabularyListKeys[this.vocabularyListKeys.length - 1];
      const vocabularyListKey = parseInt(lastVocabularyListKey, 10) + 1;

      const vocabulary = {
        [this.userLanguages.base]: this.baseTopic,
        color: this.color,
        lang: `${this.userLanguages.base}-${this.userLanguages.learning}`,
        vocabularies: [
          ...this._.cloneDeep(this.selectedItems),
        ],
      };

      await this.mergeData(VOCABULARIES, { [vocabularyListKey]: vocabulary });
    },
    async copySelectedItemsToSelectedTopic() {
      const topicIndex = this._.findIndex(this.vocabularyList, {
        [this.userLanguages.base]: this.localTopic,
      });

      this.selectedItems.forEach((item) => {
        if (item) {
          this.vocabularyList[topicIndex].vocabularies.push(item);
        }
      });

      const vocabularyListKey = this.vocabularyListKeys[topicIndex];

      await this.mergeData(VOCABULARIES, { [vocabularyListKey]: this.vocabularyList[topicIndex] });
    },
    async deleteSelectedItemsFromPreviousTopic() {
      const listItemToDeleteFromIndex = this._.findIndex(this.vocabularyList, {
        [this.userLanguages.base]: this.topic,
      });

      const vocabulariesToDeleteFrom = this.vocabularyList[listItemToDeleteFromIndex].vocabularies;

      this.selectedItems.forEach((item) => {
        const vocabularyToDeleteIndex = this._.findIndex(vocabulariesToDeleteFrom, { ...item });

        this.$delete(
          this.vocabularyList[listItemToDeleteFromIndex].vocabularies,
          vocabularyToDeleteIndex,
        );
      });

      const vocabularyListKey = this.vocabularyListKeys[listItemToDeleteFromIndex];

      await this.mergeData(VOCABULARIES, {
        [vocabularyListKey]: this.vocabularyList[listItemToDeleteFromIndex],
      });
    },
    async moveSelectedItems() {
      this.showLoader();

      this.showModal = false;

      const isNewTopic = this.baseTopic === this.localTopic;
      if (isNewTopic) {
        await this.moveVocabularyToNewTopic();
      } else {
        await this.copySelectedItemsToSelectedTopic();
      }
      await this.deleteSelectedItemsFromPreviousTopic();

      this.$emit('updated:move-items');

      this.hideLoader();
    },
    getTopics() {
      this.topics = this.vocabularyListByLanguage.map((item) => item[this.userLanguages.base]);
    },
  },
};
</script>
